/** Parsed image evidence used by the dashboard; no server-provided URL is trusted. */
export type NoteImageMetadata =
  | { readonly kind: "absent" }
  | { readonly kind: "invalid" }
  | {
      readonly kind: "ready";
      readonly id: string;
      readonly width: number;
      readonly height: number;
      readonly reconstructed: boolean;
      /** Where the noted elements sit on the image; null without capture data. */
      readonly targets: readonly ImageBox[] | null;
    };

/** A box on the image as fractions of its width and height, 0 to 1. */
export interface ImageBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** Parse only the stored image fields that the dashboard needs to display. */
export function parseNoteImage(value: unknown): NoteImageMetadata {
  if (value === null || value === undefined) return { kind: "absent" };
  if (
    typeof value !== "object" ||
    !("id" in value) ||
    typeof value.id !== "string" ||
    value.id.length === 0 ||
    !("width" in value) ||
    !imageDimension(value.width) ||
    !("height" in value) ||
    !imageDimension(value.height)
  )
    return { kind: "invalid" };
  const capture = "capture" in value ? value.capture : null;
  return {
    kind: "ready",
    id: value.id,
    width: value.width,
    height: value.height,
    reconstructed:
      typeof capture === "object" &&
      capture !== null &&
      "method" in capture &&
      capture.method === "dom-reconstruction",
    targets: captureTargets(capture),
  };
}

/**
 * Project each visible target's bounds through the crop rectangle, both in
 * page CSS pixels, onto the image. Older images without capture geometry, or
 * with geometry this parser does not recognise, get no overlay.
 */
function captureTargets(capture: unknown): readonly ImageBox[] | null {
  if (typeof capture !== "object" || capture === null) return null;
  const crop =
    "crop_viewport_rect" in capture ? rect(capture.crop_viewport_rect) : null;
  const targets = "targets" in capture ? capture.targets : null;
  if (crop === null || !Array.isArray(targets)) return null;
  const boxes: ImageBox[] = [];
  for (const target of targets as unknown[]) {
    if (
      typeof target !== "object" ||
      target === null ||
      !("kind" in target) ||
      (target.kind !== "visible" && target.kind !== "partial") ||
      !("visible_bounds" in target)
    )
      continue;
    const bounds = rect(target.visible_bounds);
    if (bounds === null) continue;
    const box = {
      left: clamp((bounds.x - crop.x) / crop.width),
      top: clamp((bounds.y - crop.y) / crop.height),
      right: clamp((bounds.x + bounds.width - crop.x) / crop.width),
      bottom: clamp((bounds.y + bounds.height - crop.y) / crop.height),
    };
    if (box.right > box.left && box.bottom > box.top) boxes.push(box);
  }
  return boxes.length === 0 ? null : boxes;
}

interface Rect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

function rect(value: unknown): Rect | null {
  if (typeof value !== "object" || value === null) return null;
  const { x, y, width, height } = value as Record<string, unknown>;
  return typeof x === "number" &&
    typeof y === "number" &&
    typeof width === "number" &&
    typeof height === "number" &&
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    Number.isFinite(width) &&
    Number.isFinite(height) &&
    width > 0 &&
    height > 0
    ? { x, y, width, height }
    : null;
}

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Build a same-origin private read path from resource identifiers, never a URL. */
export function noteImagePath(siteId: string, noteId: string): string {
  return `/v1/appnotes/sites/${encodeURIComponent(siteId)}/notes/${encodeURIComponent(noteId)}/screenshot`;
}

function imageDimension(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}
